<template>
  <div :class="$style['payments-layout']">
    <LoadingScreen v-if="isLoading" :class="$style['spinner']" />
    <div v-else>
      <!-- div w/ id 'in-app-msg' is used in cio for in app messages -->
      <div id="in-app-msg"></div>
      <TheGlobalBanners />
      <template v-if="isHome">
        <RevenueAccountConnectionModal />
        <div :class="$style.capacitySection">
          <div :class="$style.capacityContainer">
            <CapacityContainer
              :show-self-reported="showSelfReportedCapacity"
              :error-type="errorType"
              @joinWaitlist="
                openPayAnInvoiceModal(ctaSources.joinWaitlistBanner)
              "
              @openPayInvoice="
                handleSubmitAnInvoiceClick(ctaSources.userCapacity)
              "
              @finishApplicationCtaClicked="handleFinishApplicationCtaClick"
            />
          </div>
          <FeatureSpotlight
            @spotlightClicked="({ slide }) => handleSpotlightClick(slide)"
          />
        </div>

        <EmbedHowItWorksPage v-if="!hasSubmittedBill" />

        <!-- Payments table -->
        <section v-if="hasSubmittedBill" :class="$style['page-section']">
          <div
            :class="[$style.section, $style['payments-nav-wrapper']]"
            data-cy="bills-table-section"
          >
            <PaymentsNav />
            <div :class="$style['payments-nav-cta-group']">
              <div :class="$style['payments-nav-buttons']">
                <DSButton
                  v-if="disableInvoiceUpload"
                  :label="waitlistCtaLabel"
                  @click="openDisabledInvoiceModal"
                />
                <DSButton
                  v-else-if="
                    $route.name !== RouteNames.MCA.VENDOR_LIST &&
                    userCanSubmitInvoices
                  "
                  :label="payAnInvoiceModalButtonText"
                  @click="handleSubmitAnInvoiceClick(ctaSources.userCapacity)"
                />
                <DSButton
                  v-if="$route.name === RouteNames.HOME.INITIAL"
                  icon="pi pi-filter-slash"
                  class="p-button-outlined"
                  label="Clear Filters"
                  @click="resetTableFilters"
                />
              </div>
              <a :href="addLocaleParam(faqLink)" target="_blank">
                {{ $t('components.paymentsNav.faqs') }}
              </a>
            </div>
            <router-view
              v-slot="{ Component }"
              :section="$route.name"
              @newPayment="handleSubmitAnInvoiceClick(ctaSources.userCapacity)"
            >
              <component :is="Component" ref="view" />
            </router-view>
          </div>
        </section>
      </template>
      <div v-if="isHome && showDisclaimer" :class="$style['bnpl-disclaimer']">
        <p>
          {{ $t('account.pages.pageVendors.disclaimer') }}
        </p>
      </div>
      <div v-if="isHome">
        <p :class="$style['vendor-logo-container']">
          <strong>{{
            $t('account.pages.pageVendors.worksGreatWithVendors')
          }}</strong>
          <img :src="googleLogo" alt="Google" />
          <img :src="metaLogo" alt="Meta" />
          <img :src="amazonLogo" alt="Amazon" />
          <img :src="microsoftLogo" alt="Microsoft" />
          <img :src="klavioLogo" alt="Klavio" />
          <img :src="flexportLogo" alt="Flexport" />
          <img :src="dhlLogo" alt="DHL" />
        </p>
        <Message severity="info" icon="pi-lock" :closable="false">
          <h3>
            {{ $t('account.pages.pageVendors.bankGradeEncryption') }}
          </h3>
          <p>
            {{ $t('account.pages.pageVendors.allInformationTraveling') }}
          </p>
        </Message>
      </div>
      <template v-if="!isHome">
        <router-view
          :section="$route.name"
          @pay-an-invoice="
            handleSubmitAnInvoiceClick(ctaSources.repaymentsPage)
          "
          @disable-invoice-upload-modal="openDisabledInvoiceModal"
        />
        <div v-if="isRepayments" :class="$style['bnpl-disclaimer']">
          <p>
            {{ $t('account.pages.subPageRepayments.allFutureDatedAmounts') }}
          </p>
        </div>
      </template>
    </div>
    <PayAnInvoiceModal
      v-if="!isBnplWaitlisted"
      ref="payAnInvoicePopup"
      :direct-user-to-bill-modal="shouldDirectUserToBillModal"
      @done="searchVendors"
    />

    <Modal
      v-model:visible="showDisabledInvoiceModal"
      header="You're on the waitlist"
      :modal="true"
      :draggable="false"
      append-to="self"
    >
      <p>
        We will let you know the moment we are ready to deliver you a great
        experience.
      </p>
    </Modal>

    <Modal
      v-model:visible="showConnectPlaidModal"
      header="Bank Account Connection Issue"
      :modal="true"
      :draggable="false"
      append-to="self"
    >
      <div :class="$style['modal-content']">
        <div class="icon-set">
          <img :src="IconConnectPlaid" alt="ConnectPlaid" />
        </div>
        <p>
          There is an issue with your Plaid bank connection and it is a
          requirement for funding. To ensure you remain eligible for funding
          connect with Plaid to link your bank accounts and resubmit any
          declined invoices or cash advances as a result.
        </p>
        <p>
          Need help? Contact us at
          <a href="mailto:support@clear.co">support@clear.co</a> or
          1-(415)-610-5166 10am-6pm EST Monday to Friday.
        </p>
        <div class="plaid-btn">
          <DSButton label="Restore connection" @click="connectPlaid" />
        </div>
      </div>
    </Modal>

    <SuccessModal
      :visible="showPreliminaryCapacityModal"
      title="Your Preliminary Funding Capacity is Ready!"
      subtitle="Submit a request to receive your Approved Funding Capacity."
      :button-label="payAnInvoiceModalButtonText"
      @submit-modal="
        openPayAnInvoiceModal();
        showPreliminaryCapacityModal = false;
      "
      @hide="closePreliminaryModal"
    >
      <img :src="ReportIcon" alt="report" />
    </SuccessModal>

    <Modal
      v-model:visible="showReconnectExternalAccount"
      header="Reconnecting your Shopify account"
      :modal="true"
      :draggable="false"
      append-to="self"
    >
      <div :class="$style['modal-content']">
        <div class="icon-set">
          <img
            :src="IconReconnectExternaAccount"
            alt="ReconnectExternaAccount"
          />
        </div>
        <p>
          Your Shopify account is currently disconnected. To ensure you remain
          eligible for funding and/or compliant with your agreement with
          Clearco, please proceed with reconnecting it.
        </p>
        <p>
          Need help? Contact us at
          <a href="mailto:support@clear.co">support@clear.co</a> or
          1-(415)-610-5166 10am-6pm EST Monday to Friday.
        </p>
        <div class="plaid-btn">
          <DSButton
            label="Restore connection"
            @click="reconnectExternalAccount"
          />
        </div>
      </div>
    </Modal>

    <template v-if="showCapacityApprovedModal">
      <RollingCapacityModal
        v-if="showRollingCapacityModal"
        :visible="showCapacityApprovedModal"
        @submit-modal="
          closeCapacityApprovedModal();
          handleSubmitAnInvoiceClick();
        "
        @hide="closeCapacityApprovedModal"
      />

      <SuccessModal
        v-else
        :visible="showCapacityApprovedModal"
        title="Your Approved Funding Capacity is Ready!"
        data-cy="capacity-approved-modal"
        subtitle="Take advantage by submitting funding requests."
        :button-label="payAnInvoiceModalButtonText"
        @submit-modal="
          closeCapacityApprovedModal();
          handleSubmitAnInvoiceClick();
        "
        @hide="closeCapacityApprovedModal"
      >
        <img :src="ReportIcon" alt="report" />
      </SuccessModal>
    </template>

    <template v-if="showRollingFundingWizard">
      <RollingFundingWizard
        :visible="showRollingFundingWizard"
        @close="closeRollingFundingWizard"
      />
    </template>

    <SignatoryModal ref="signatoryModal" />

    <Modal
      v-model:visible="showReferralModal"
      header="Welcome to Clearco"
      :modal="true"
      :draggable="false"
      append-to="self"
      @hide="closeReferralModal"
    >
      <div :class="$style['referral-modal']">
        <p class="m-t-10">
          Since you were referred by {{ business.referringBusinessName }}, we
          have pre-filled some of your information and completed a few
          onboarding steps to help you get started faster. There are just a
          couple more steps to complete before you can submit your invoices and
          cash advances to get funded:
        </p>
        <div :class="$style.steps">
          <div :class="$style.step">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="12" fill="#7F30FF" />
              <path
                d="M11.7205 17V10.868H10.1365V9.668H10.7005C11.5165 9.668 11.8645 9.452 11.8645 8.528H13.1965V17H11.7205Z"
                fill="white"
              />
            </svg>
            <div :class="$style['step-text']">Complete Business Profile</div>
          </div>
          <div :class="$style.step">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="12" fill="#7F30FF" />
              <path
                d="M8.89908 17V15.632C10.1231 14.996 13.4111 12.92 13.4111 10.916C13.4111 10.088 12.8711 9.536 12.0791 9.536C11.2391 9.536 10.6031 9.992 10.5791 11.504L9.05508 11.144C9.09108 9.14 10.6031 8.324 12.0791 8.324C13.7351 8.324 14.9231 9.368 14.9231 10.916C14.9231 12.992 12.6071 14.864 10.8671 15.812H15.0791V17H8.89908Z"
                fill="white"
              />
            </svg>
            <div :class="$style['step-text']">Connect Revenue Account</div>
          </div>
        </div>

        <p>We’ll guide you every step of the way—let’s get started!</p>

        <div :class="$style.buttons">
          <DSButton
            label="Back"
            :class="$style['referral-modal-button']"
            class="p-button-outlined"
            @click="closeReferralModal"
          />
          <DSButton
            label="Get Started"
            :class="$style['referral-modal-button']"
            @click="routeToBizProfile"
          />
        </div>
      </div>
    </Modal>

    <CollaborateModal
      :visible="showCollaborateModal"
      @close="showCollaborateModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Message from 'primevue/message';
import sendUtmParamsToSegment from '@/composables/utm';

import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import addLocaleParam from '@/utils/add-locale-param';
import PaymentsNav from '@/pages/payments/components/PaymentsNav.vue';
import PayAnInvoiceModal from '@/components/PayAnInvoiceModal/index.vue';
import LoadingScreen from '@/components/LoadingScreen.vue';
import TheGlobalBanners from '@/components/banners/TheGlobalBanners.vue';
import DSButton from 'primevue/button';

import {
  CLEARPAY_CTA_SOURCES,
  BillPaymentMethods,
  PAYMENTS_STATE_MODULE,
  HELP_CENTER_URL,
} from '@/data/payments';
import { STRATEGY_TYPES } from '@/data/capacity';
import { RouteNames } from '@/data/routes';
import { useMeta } from 'vue-meta';
import { subMonths, isAfter, subHours } from 'date-fns';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { isLegacyNativeConnection } from '@/utils/external-accounts';

import Modal from '@/components/shared/Dialog.vue';
import EmbedHowItWorksPage from '@/pages/embed/EmbedHowItWorksPage.vue';
import CapacityContainer from '@/components/CapacityContainer.vue';
import SuccessModal from '@/components/modals/SuccessModal.vue';
import RollingCapacityModal from '@/components/modals/RollingCapacityModal.vue';
import SignatoryModal from '@/components/modals/SignatoryModal.vue';
import CollaborateModal from '@/components/modals/CollaborateModal.vue';
import RevenueAccountConnectionModal from '@/components/modals/RevenueAccountConnectionModal.vue';
import RollingFundingWizard from '@/components/modals/RollingFundingWizard.vue';
import FeatureSpotlight from '@/components/FeatureSpotlight.vue';

import IconConnectPlaid from '@/assets/icons/connect-plaid.svg?inline';
import IconReconnectExternaAccount from '@/assets/icons/reconnect-external-account.svg?inline';
import ReportIcon from '@/assets/icons/report.svg?inline';

import googleLogo from '@/assets/logos/google.png';
import metaLogo from '@/assets/logos/meta.png';
import amazonLogo from '@/assets/logos/amazon.png';
import microsoftLogo from '@/assets/logos/microsoft.png';
import klavioLogo from '@/assets/logos/klavio.png';
import flexportLogo from '@/assets/logos/flexport.png';
import dhlLogo from '@/assets/logos/dhl.png';
import { BillType } from '@/@types/bill.types';
import { useRollingFundingInquiries } from '@/composables/useRollingFundingInquiries';
import { useToast } from '@/composables/useToast';

export default {
  components: {
    PaymentsNav,
    DSButton,
    PayAnInvoiceModal,
    SuccessModal,
    RollingCapacityModal,
    RollingFundingWizard,
    LoadingScreen,
    Message,
    Modal,
    EmbedHowItWorksPage,
    CapacityContainer,
    SignatoryModal,
    CollaborateModal,
    TheGlobalBanners,
    RevenueAccountConnectionModal,
    FeatureSpotlight,
  },
  setup() {
    useMeta({
      title: 'Dashboard | Clearco',
    });
    const toast = useToast();
    sendUtmParamsToSegment();
    const { fetchLatestRollingFundingInquiry } = useRollingFundingInquiries();
    return { fetchLatestRollingFundingInquiry, toast };
  },
  data() {
    return {
      IconConnectPlaid,
      ReportIcon,
      IconReconnectExternaAccount,
      isLoading: false,
      ctaSources: CLEARPAY_CTA_SOURCES,
      faqLink: HELP_CENTER_URL,
      showDisabledInvoiceModal: false,
      showConnectPlaidModal: false,
      showReconnectExternalAccount: false,
      sumbitAnInvoiceClicked: false,
      showPreliminaryCapacityModal: false,
      showCapacityApprovedModal: false,
      showRollingFundingWizard: false,
      showReferralModal: false,
      showCollaborateModal: false,
      googleLogo,
      metaLogo,
      amazonLogo,
      microsoftLogo,
      klavioLogo,
      flexportLogo,
      dhlLogo,
      RouteNames,
      STRATEGY_TYPES,
    };
  },
  computed: {
    ...mapGetters([
      'user',
      'businessId',
      'business',
      'bills',
      'hasActiveSalesAccounts',
      'marketingAccountsConnected',
      'isBnplWaitlisted',
      'bankingAccountsConnected',
      'isBusinessDetailsStepComplete',
      'isBusinessOwnersStepComplete',
      'businessCapacity',
      'userCanSubmitInvoices',
      'businessCapacityGenerated',
      'disableInvoiceUpload',
      'isPrimaryBankAccountDisconnected',
      'shopifyAccounts',
      'businessOnPlaidBypass',
      'maxCapacityExceeded',
      'hasActiveDocumentRequests',
      'intendedPrimaryOrPrimaryBankAccount',
      'isHome',
      'userCanSubmitInvoices',
      'isUseShopifyNativeEnabled',
    ]),
    ...mapRequestStatuses({
      searchVendorsRequest: 'SEARCH_VENDORS',
      businessCapacityRequest: 'GET_BUSINESS_CAPACITY',
      billsRequest: 'GET_BILLS',
      createPayInvoiceRequest: 'CREATE_PAY_INVOICE',
      getExternalAccountsRequest: 'FETCH_USER_EXTERNAL_ACCOUNTS',
    }),
    showRollingCapacityModal() {
      return (
        this.businessCapacity?.exposureStrategy ===
        this.STRATEGY_TYPES.prorata_origination
      );
    },
    shouldShowPreliminaryCapacityModal() {
      return (
        this.businessCapacityGenerated &&
        !this.maxCapacityExceeded &&
        !this.businessCapacity.approvedAt &&
        !this.preliminaryCapacityModalViewed &&
        this.userCanSubmitInvoices
      );
    },
    preliminaryCapacityModalViewed() {
      return (
        LocalStorage.getItem(
          LocalStorageKeys.VIEWED_PRELIMINARY_CAPACITY_MODAL,
        ) === this.businessCapacity?.id?.toString()
      );
    },
    showDisclaimer() {
      const bnplBills = this.bills.filter(
        (bill) => bill.paymentMethod === BillPaymentMethods.BNPL,
      );
      return bnplBills?.length !== 0;
    },
    isRepayments() {
      return (
        this.$route.name === this.RouteNames.PAYMENTS.INDEX ||
        this.$route.name === this.RouteNames.PAYMENTS.UPCOMING
      );
    },
    hasSubmittedBill() {
      return this.bills && this.bills.length > 0;
    },
    payAnInvoiceModalButtonText() {
      return this.isBnplWaitlisted
        ? this.$t('account.pages.pageVendors.joinTheWaitlist')
        : this.$t('account.pages.pageVendors.accessWorkingCapital');
    },
    showSelfReportedCapacity() {
      return !this.businessCapacityGenerated;
    },
    errorType() {
      return this.businessCapacityRequest.error?.type;
    },
    waitlistCtaLabel() {
      return this.$t('account.pages.pageVendors.joinTheWaitlist');
    },
    needsToMigrateShopifyAccount() {
      return (
        !this.isUseShopifyNativeEnabled &&
        !!this.shopifyAccounts.find((account) =>
          isLegacyNativeConnection(account),
        )
      );
    },
    shouldDirectUserToBillModal() {
      return Object.values(BillType).includes(this.$route.query.type);
    },
  },
  async beforeMount() {
    const statementStartDate = subMonths(new Date(), 6).toISOString();
    this.isLoading = true;
    await this.$store.dispatchApiAction('FETCH_USER_CARDS', {
      canceled: false,
    });

    await this.$store.dispatchApiAction('FETCH_USER_EXTERNAL_ACCOUNTS');
    await this.$store.dispatchApiAction('FETCH_USER_BANK_ACCOUNTS');

    await this.$store.dispatchApiAction('FETCH_USER_UPLOADS');

    await this.$store.dispatch('setCurrentStateModule', PAYMENTS_STATE_MODULE);

    await this.$store.dispatchApiAction('GET_BUSINESS_CAPACITY');
    await this.$store.dispatchApiAction('GET_BILLS');
    await this.$store.dispatchApiAction('GET_DEBT_OBLIGATIONS');
    await this.$store.dispatchApiAction('GET_DOCUMENT_REQUESTS');
    await this.$store.dispatchApiAction('GET_BANK_STATEMENTS_CHECK', {
      statementStartDate,
      bankAccountNumber:
        this.intendedPrimaryOrPrimaryBankAccount?.accountNumber,
    });
    await this.fetchLatestRollingFundingInquiry();

    this.showPreliminaryCapacityModal = this.shouldShowPreliminaryCapacityModal;

    analytics.identify(this.user.id, {
      hasConnectedSalesAccounts: this.hasActiveSalesAccounts,
      hasMarketingAccountConnected: this.marketingAccountsConnected,
      hasConnectedBankAccounts: this.bankingAccountsConnected,
      hasFilledBusinessDetails: this.isBusinessDetailsStepComplete,
      hasFilledOwnerInformation: this.isBusinessOwnersStepComplete,
    });
    analytics.track(SEGMENT_EVENTS.VIEWED_DASHBOARD, {
      businessId: this.business.id,
      businessName: this.business.name,
    });
    this.isLoading = false;

    this.checkCapacityApprovedModalStatus();

    await this.$store.dispatch('UPDATE_ALL_PAYMENTS_BIZ_PROFILE_STEPS');
  },
  async mounted() {
    await this.searchVendors();

    if (this.$route.query['upload-invoice']) {
      this.$refs.payAnInvoicePopup?.open();
    }

    await this.$store.dispatchApiAction('GET_FISCAL_DOCUMENT_CHECKS', {
      businessId: this.businessId,
    });

    this.$nextTick(() => {
      this.toggleSignatoryModal();
    });

    if (this.displayReferralModal()) {
      this.showReferralModal = true;
    }
    const {
      warn: warnKey,
      error: errorKey,
      ...remainingQuery
    } = this.$route.query;
    if (warnKey && this.$te(warnKey)) this.toast.warn(this.$t(warnKey));
    if (errorKey && this.$te(errorKey)) this.toast.error(this.$t(errorKey));
    this.$router.replace({ query: remainingQuery });
  },
  watch: {
    businessCapacityRequest(newValue) {
      if (
        this.$route.name === RouteNames.HOME.INITIAL &&
        newValue.isSuccess &&
        this.isPrimaryBankAccountDisconnected &&
        !this.showReconnectExternalAccount &&
        !this.businessOnPlaidBypass
      ) {
        this.showConnectPlaidModal = true;
      }
    },
    getExternalAccountsRequest(newValue) {
      if (
        this.needsToMigrateShopifyAccount &&
        this.$route.name === RouteNames.HOME.INITIAL &&
        newValue.isSuccess
      ) {
        this.showReconnectExternalAccount = true;
      }
    },
    '$route.name': function (newRoute) {
      const homePage = this.RouteNames.HOME.INITIAL;
      if (newRoute === homePage) {
        this.toggleSignatoryModal();
      }
    },
    userCanSubmitInvoices: {
      async handler() {
        // Directly open the invoice or cash advance modal
        if (this.$route.params.billId === 'new' && this.userCanSubmitInvoices) {
          this.$refs.payAnInvoicePopup?.open();

          // Directly open the invoice or cash advance view if passed in the query parameters
          if (this.shouldDirectUserToBillModal) {
            await this.$store.dispatch(
              'UPDATE_BILL_FLOW_TYPE',
              this.$route.query.type,
            );
          }

          // Clean the URL so that the user can use 'Go Back', and overall navigate the app as before
          this.$router.replace({
            path: this.$route.path.replace('/new', ''),
            query: {}, // Remove `type` query parameter
          });
        }
      },
    },
    isLoading(newValue) {
      if (!newValue) {
        this.checkShowCollaborateModal();
      }
    },
  },
  methods: {
    addLocaleParam,
    handleSpotlightClick(slide) {
      if (slide === 'Rolling Funding') {
        this.showRollingFundingWizard = true;
      }
    },
    closePreliminaryModal() {
      LocalStorage.setItem(
        LocalStorageKeys.VIEWED_PRELIMINARY_CAPACITY_MODAL,
        this.businessCapacity.id.toString(),
      );
      this.showPreliminaryCapacityModal = false;
    },
    closeCapacityApprovedModal() {
      LocalStorage.setItem(
        LocalStorageKeys.DISMISSED_CAPACITY_APPROVED_MODAL,
        this.businessCapacity.id,
      );
      this.showCapacityApprovedModal = false;
    },
    closeRollingFundingWizard() {
      this.showRollingFundingWizard = false;
    },
    async searchVendors() {
      await this.$store.dispatchApiAction('SEARCH_VENDORS', {
        // TODO: Revisit this limit once /vendors API has been reviewed.
        limit: 200,
        businessId: this.businessId,
      });
    },
    openPayAnInvoiceModal(source) {
      this.$refs.payAnInvoicePopup?.open();

      analytics.track(SEGMENT_EVENTS.submit_invoice_cta_clicked, {
        businessId: this.businessId,
        businessName: this.business.name,
      });
      analytics.track(SEGMENT_EVENTS.if_clicked_add_bill, {
        businessId: this.businessId,
        businessName: this.business.name,
        origin: this.ctaSources.paymentsList,
      });

      if (this.isBnplWaitlisted) {
        // TODO: implement Slack message
        // TODO: store local storage to avoid duplicate clicks
        analytics.track(SEGMENT_EVENTS.CLEARPAY_BNPL_WAITLISTED, {
          email: this.user.email,
          name: this.user.fullName,
          businessId: this.businessId,
          businessName: this.business.name,
        });
      }
    },
    openDisabledInvoiceModal() {
      this.showDisabledInvoiceModal = true;
    },
    routeToBizProfile() {
      this.closeReferralModal();
      return this.$router.push({
        name: RouteNames.PROFILE.INDEX,
      });
    },
    handleSubmitAnInvoiceClick(source) {
      this.sumbitAnInvoiceClicked = true;
      this.openPayAnInvoiceModal(source);
    },
    resetTableFilters() {
      this.$refs.view.resetFilters();
    },
    connectPlaid() {
      this.$router.push({
        name: RouteNames.BANKING.INDEX,
      });
      this.showConnectPlaidModal = false;
    },
    reconnectExternalAccount() {
      this.$router.push({
        name: RouteNames.CONNECTED_ACCOUNTS.INDEX,
      });
      this.showReconnectExternalAccount = false;
    },
    handleFinishApplicationCtaClick() {
      analytics.track(SEGMENT_EVENTS.finish_application_cta_clicked, {
        businessId: this.business.id,
        businessName: this.business.name,
      });
      this.$router.push({
        name: RouteNames.PROFILE.INDEX,
      });
    },
    checkCapacityApprovedModalStatus() {
      const capcityExpiryDate = this.businessCapacity?.expiresAt;
      const storedCapacityId = LocalStorage.getItem(
        LocalStorageKeys.DISMISSED_CAPACITY_APPROVED_MODAL,
      );

      const isCapacityExpired =
        capcityExpiryDate && new Date(capcityExpiryDate) < new Date();
      const isCapacityNotApproved = !this.businessCapacity?.approvedAt;
      const isModalAlreadyDismissed =
        Number(storedCapacityId) === this.businessCapacity.id;

      if (
        isCapacityNotApproved ||
        isCapacityExpired ||
        isModalAlreadyDismissed ||
        this.hasActiveDocumentRequests ||
        this.isPrimaryBankAccountDisconnected ||
        this.maxCapacityExceeded
      ) {
        return;
      }

      this.showCapacityApprovedModal = this.userCanSubmitInvoices;
    },
    toggleSignatoryModal() {
      if (this.$refs.signatoryModal) {
        this.$refs.signatoryModal.toggleModal();
      }
    },
    closeReferralModal() {
      this.showReferralModal = false;
      LocalStorage.setItem(LocalStorageKeys.DISMISSED_REFERRAL_MODAL, true);
    },
    displayReferralModal() {
      const wasReferralModalDismissed = LocalStorage.getItem(
        LocalStorageKeys.DISMISSED_REFERRAL_MODAL,
      );

      const CUTOFF_HOURS = 48;
      const businessCreated = new Date(this.business.createdAt);
      const cutOffDate = subHours(new Date(), CUTOFF_HOURS);

      return (
        isAfter(businessCreated, cutOffDate) &&
        !wasReferralModalDismissed &&
        this.business.referringBusinessName
      );
    },
    checkShowCollaborateModal() {
      const dismissed = LocalStorage.getItem(
        LocalStorageKeys.DISMISSED_COLLABORATE_MODAL,
      );
      if (!dismissed) {
        this.showCollaborateModal = true;
      }
    },
  },
};
</script>
<style lang="less" scoped>
.icon-set {
  margin-bottom: 50px;
  margin-top: 15px;
  img {
    height: 45px;
    width: 100%;
    display: inline-block;
  }
}
.plaid-btn {
  text-align: center;
  margin-top: 40px;
}

.accordion-section {
  margin-bottom: 25px;
}

:deep(
  .accordion-section .p-accordion .p-accordion-header .p-accordion-header-link
),
:deep(
  .accordion-section
    .p-accordion
    .p-accordion-header:not(.p-disabled).p-highlight
    .p-accordion-header-link
) {
  color: #393939;
  background-color: @color-white;
}
</style>
<style lang="less" module>
.payments-layout {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 20px;
  font-family: @regular-font;
  font-weight: 300;

  h3 {
    font-family: @regular-font;
    font-weight: 700;
    font-size: 21px;
  }

  [class^='wrapper'] {
    max-width: none;
  }
}

.section {
  font-family: @regular-font;
  padding: 15px 30px;
  margin-bottom: 25px;
  background: @color-white;
  border: 1px solid @color-lightgray-300;
  box-sizing: border-box;
  border-radius: 4px;
  flex: 1 0 0;
}

.vendor-container {
  border: none;
  margin-top: 30px;
}

.columns {
  display: flex;
  flex-direction: row;
  margin-bottom: 25px;

  @media screen and (max-width: 650px) {
    justify-content: space-evenly;
  }
}

.icon-hand-card {
  width: 31px;
  height: 21px;
}

.icon-institution {
  width: 19px;
  height: 21px;
}

.pay-bill {
  background: @black;

  div[class^='round-icon'] {
    color: @black;
  }
}

a.no-underline {
  text-decoration: none;
}

.vendor-logo-container {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;

  @media screen and (max-width: 500px) {
    gap: 25px;
    flex-wrap: wrap;
    justify-content: center;

    strong {
      flex: 100%;
    }
  }

  img {
    vertical-align: middle;
    height: 19px;
    width: auto;
  }
}

.payments-nav-wrapper {
  div[class*='vendor-container'] {
    margin: 0;
  }

  .payments-nav-cta-group {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin: 20px 0;

    @media (min-width: 500px) {
      align-items: center;
    }

    @media screen and (max-width: 500px) {
      flex-direction: column;

      a {
        margin-top: 10px;
      }
    }
  }
}

.payments-nav-buttons {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  [class^='p-button'] + [class^='p-button'] {
    margin-left: 0.625rem;
  }

  @media screen and (max-width: 420px) {
    flex-direction: column;
    [class^='p-button'] + [class^='p-button'] {
      margin-left: 0;
      margin-top: 0.3125rem;
    }
  }
}

.user-capacity-container {
  border: 1px solid @color-grey-200;
  border-radius: 4px;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background-color: @color-white;
  padding: 36px 30px 20px;
  margin-bottom: 24px;

  &.bill-capacity {
    padding: 10px 25px 10px;

    @media screen and (max-width: 500px) {
      padding: 10px 15px 10px;
    }
  }
}

.spinner {
  > div {
    margin: 50px auto !important;
  }
}

.bnpl-disclaimer {
  font-size: 10px;
  color: gray;
}
.modal-content {
  max-width: 700px;
  text-align: center;
}

.capacity-modal-icon {
  font-size: 80px;
  margin-top: 25px;
}

.referral-modal {
  max-width: 665px;
  display: flex;
  flex-direction: column;
}

.step {
  display: flex;
  padding: 12px 0;

  .step-text {
    padding-left: 14px;
  }
}

.buttons {
  display: flex;
  justify-content: space-evenly;
  gap: 15px;
  margin-top: 20px;

  .referral-modal-button {
    width: 100%;
  }
}

.capacitySection {
  display: flex;
  gap: 16px;

  @media (max-width: 1320px) {
    flex-direction: column;
  }
}

.capacityContainer {
  flex: 1;
}
</style>
